export interface WaveformBar {
  height: number;
  played?: boolean;
}

export function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64.split(',')[1]);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: mimeType });
}

export function blobToFile(theBlob: Blob, fileName: string): File {
  const b: any = theBlob;
  b.lastModifiedDate = new Date();
  b.name = fileName;
  return <File>theBlob;
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Converts a File or Blob object to a base64 data URL string.
 * This correctly preserves the file's original MIME type.
 * @param file The file to convert.
 * @returns A promise that resolves with the base64 data URL string.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const result = event.target?.result as ArrayBuffer;
      resolve(result);
    };

    reader.onerror = (event) => {
      reject(event.target?.error?.message);
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * Gets the accurate duration of an audio File or Blob using the Web Audio API.
 * This is the most reliable method for determining the length of browser-generated
 * audio files like those from MediaRecorder.
 * @param audioFile The audio file (Blob or File object) to process.
 * @returns A promise that resolves with the duration in seconds.
 */
export function getDurationFromFile(audioFile: Blob | File): Promise<number> {
  return new Promise(async (resolve, reject) => {
    const audioContext = new AudioContext();
    try {
      const arrayBuffer = await audioFile.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      resolve(audioBuffer.duration);
    } catch (e) {
      console.error('Error decoding audio data:', e);
      reject(e);
    } finally {
      if (audioContext.state !== 'closed') {
        audioContext.close();
      }
    }
  });
}

export function generateWaveformFromBuffer(audioBuffer: AudioBuffer, barsCount: number): WaveformBar[] {
  const rawData = audioBuffer.getChannelData(0);
  const samplesPerBar = Math.floor(rawData.length / barsCount);
  const newBars: WaveformBar[] = [];

  for (let i = 0; i < barsCount; i++) {
    let maxAmp = 0;
    const start = i * samplesPerBar;
    for (let j = start; j < start + samplesPerBar; j++) {
      const amp = Math.abs(rawData[j]);
      if (amp > maxAmp) maxAmp = amp;
    }
    // Normalize the height to a percentage using a power scale for better visuals
    const height = Math.pow(maxAmp, 0.4) * 100;
    newBars.push({ height: Math.min(100, Math.max(5, height)) }); // Clamp height
  }
  return newBars;
}

/**
 * Generates a placeholder waveform with random bar heights.
 * @param barsCount The total number of bars to generate.
 * @returns An array of WaveformBar objects with random heights.
 */
export function generateRandomWaveform(barsCount: number): WaveformBar[] {
  return Array.from({ length: barsCount }, () => ({
    height: Math.random() * 60 + 10,
  }));
}

/**
 * Re-encodes a raw AudioBuffer into a Blob representing a .wav file.
 * This is necessary for saving concatenated audio segments.
 *
 * @param buffer The AudioBuffer containing the raw audio data from the Web Audio API.
 * @returns A Blob object with the MIME type 'audio/wav'.
 */
export function audioBufferToBlob(buffer: AudioBuffer): Blob {
  const numOfChan = buffer.numberOfChannels;
  const length = buffer.length * numOfChan * 2 + 44; // 2 bytes per sample
  const newBuffer = new ArrayBuffer(length);
  const view = new DataView(newBuffer);
  const channels = [];
  let i;
  let sample;
  let offset = 0;
  let pos = 0;

  // Helper function to write a string to the DataView
  const writeString = (view: DataView, offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  // WAV File Header
  writeString(view, offset, 'RIFF');
  offset += 4;
  view.setUint32(offset, length - 8, true);
  offset += 4;
  writeString(view, offset, 'WAVE');
  offset += 4;
  writeString(view, offset, 'fmt ');
  offset += 4;
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numOfChan, true);
  offset += 2;
  view.setUint32(offset, buffer.sampleRate, true);
  offset += 4;
  view.setUint32(offset, buffer.sampleRate * numOfChan * 2, true);
  offset += 4;
  view.setUint16(offset, numOfChan * 2, true);
  offset += 2;
  view.setUint16(offset, 16, true);
  offset += 2;
  writeString(view, offset, 'data');
  offset += 4;
  view.setUint32(offset, length - offset - 4, true);
  offset += 4;

  // Get the raw data from each channel
  for (i = 0; i < numOfChan; i++) {
    channels.push(buffer.getChannelData(i));
  }

  // --- THE FIX IS HERE ---
  // The loop should iterate over the number of samples (buffer.length),
  // not the total byte length of the file.
  while (pos < buffer.length) {
    for (i = 0; i < numOfChan; i++) {
      // Interleave samples
      sample = Math.max(-1, Math.min(1, channels[i][pos]));
      sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0;
      view.setInt16(offset, sample, true);
      offset += 2;
    }
    pos++;
  }

  return new Blob([view], { type: 'audio/wav' });
}

export async function blobToBuffer(audioBlob: Blob): Promise<AudioBuffer> {
  const audioContext = new AudioContext();
  try {
    const arrayBuffer = await audioBlob.arrayBuffer();
    return await audioContext.decodeAudioData(arrayBuffer);
  } catch (error) {
    console.error('Error decoding audio data:', error);
    throw error;
  } finally {
    if (audioContext.state !== 'closed') {
      audioContext.close();
    }
  }
}

/**
 * Converts a base64 data URL string into a File object.
 * @param base64 The base64 data URL (e.g., "data:audio/webm;base64,...").
 * @param filename The desired name for the output file.
 * @returns A File object.
 */
export function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(',');
  if (arr.length < 2) {
    throw new Error('Invalid base64 string');
  }
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

/**
 * Plays a synthesized beep sound using the Web Audio API.
 * @param frequency The pitch of the beep in Hertz.
 * @param isFinalBeep If true, plays a slightly longer sound.
 */
export function playBeep(frequency = 880, isFinalBeep = false): void {
  if (typeof window === 'undefined' || !window.AudioContext) {
    return; // Don't run in a non-browser environment
  }
  const audioContext = new AudioContext();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);

  oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
  oscillator.type = 'sine';

  const duration = isFinalBeep ? 0.2 : 0.1;
  oscillator.start(audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.00001, audioContext.currentTime + duration);
  oscillator.stop(audioContext.currentTime + duration);

  setTimeout(() => {
    if (audioContext.state !== 'closed') {
      audioContext.close();
    }
  }, 300);
}

/**
 * Checks if a MediaStream has audio activity (is not completely silent).
 * Used to detect if the browser has fallen back to a silent virtual microphone.
 * @param stream The audio MediaStream to check.
 * @param durationMs How long to listen for activity before concluding it's silent.
 * @returns A promise that resolves to true if audio is detected, false otherwise.
 */
export async function checkAudioStreamActivity(stream: MediaStream, durationMs = 2000): Promise<boolean> {
  return new Promise((resolve) => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    analyser.fftSize = 256;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    let hasActivity = false;
    let settled = false;
    let checkInterval: any;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      clearInterval(checkInterval);
      source.disconnect();
      if (audioContext.state !== 'closed') audioContext.close().catch((e) => console.error(e));
    };

    const checkVolume = () => {
      analyser.getByteFrequencyData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      if (sum > 0) {
        hasActivity = true;
        cleanup();
        resolve(true);
      }
    };

    // Immediately clean up if the stream tracks are stopped externally
    stream.getTracks().forEach((track) => {
      const originalOnended = track.onended;
      track.onended = (event) => {
        cleanup();
        if (!hasActivity) resolve(false);
        // Preserve any existing onended handler
        if (typeof originalOnended === 'function') originalOnended.call(track, event);
      };
    });

    checkInterval = setInterval(checkVolume, 100);

    setTimeout(() => {
      cleanup();
      if (!hasActivity) resolve(false);
    }, durationMs);
  });
}
